/**
 * EncounterRedirect Component
 *
 * Smart redirect based on encounter status:
 * - recording.status === 'pending' | undefined → /recording?encounterId=X
 * - recording.status === 'completed' && transcription.status === 'completed' → /encounter/X/validation
 * - transcription.status === 'processing' → /encounter/X/validation (shows loading state)
 *
 * Used for deep linking - allows direct URLs to encounters that automatically
 * route to the appropriate page based on the encounter's current state.
 */

import { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { encounterService } from '@/services/encounter';
import { computeEncounterUrl } from '@/utils/encounterUrl';
import { isRecordAccessDenied } from '@/utils/recordAccess';
import RecordAccessDenied from '@/components/RecordAccessDenied';
import { Loader2, AlertCircle } from 'lucide-react';

const EncounterRedirect: React.FC = () => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  useEffect(() => {
    const determineRedirect = async () => {
      if (!encounterId) {
        setError('No encounter ID provided');
        setLoading(false);
        return;
      }

      try {
        const encounter = await encounterService.getEncounterById(encounterId);
        const url = computeEncounterUrl(encounter);
        setRedirectUrl(url);
      } catch (err) {
        if (isRecordAccessDenied(err)) {
          setAccessDenied(true);
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load encounter');
      } finally {
        setLoading(false);
      }
    };

    determineRedirect();
  }, [encounterId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading encounter...</p>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return <RecordAccessDenied backTo="/encounters" />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="mt-4 text-xl font-semibold text-gray-900">
            Unable to Load Encounter
          </h1>
          <p className="mt-2 text-gray-600">{error}</p>
          <a
            href="/dashboard"
            className="mt-6 inline-block px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Return to Dashboard
          </a>
        </div>
      </div>
    );
  }

  if (redirectUrl) {
    return <Navigate to={redirectUrl} replace />;
  }

  return null;
};

export default EncounterRedirect;
